import { Template, TemplateType } from '@dev/ee-api/ee-api-models';
import { safelyDecodeBase64Url, safelyEncodeBase64Url } from '@dev/ee-tools';
import { push, replace } from 'redux-first-history';
import { call, fork, put, select, takeLatest } from 'redux-saga/effects';

import i18n from '@/../i18n';
import { ConfigState, getConfigStore } from '@/store/config/types';
import { isEmpty } from 'lodash-es';
import { Logger } from '@/services/errors/appLogger';
import {
    copyTemplate,
    createSharedTemplate,
    deleteTemplate,
    deleteTemplateArray,
    loadTemplateList,
    previewTemplate,
    shareTemplate,
    updateTemplate,
} from '../services/ApiCategories/templates';
import { pathsParams, TemplatesPaths } from '../services/dictionaries/Paths';
import {
    AddTemplateError,
    AddTemplateSuccess,
    createSharedTemplateSuccess,
    getTemplateList,
    LoadingTemplatesError,
    LoadingTemplatesSuccess,
    ShareTemplateError,
    ShareTemplateSuccess,
    templateArrayDeleteError,
    templateArrayDeleteSuccess,
    templateDeleteError,
    templateDeleteSuccess,
    templatePreviewError,
    templatePreviewSuccess,
    templateUpdateError,
    templateUpdateSuccess,
} from '../store/templateList/actions';
import {
    CreateSharedTemplateStartAction,
    LoadListStartAction,
    ShareTemplateStartAction,
    templateArrayDeleteStartAction,
    templateDeleteStartAction,
    TemplateListState,
    templatePreviewStartAction,
    TemplatesActionTypes,
} from '../store/templateList/types';
import { HideToast, ShowToast } from '../store/toast/actions';

function* ShowErrorToast(e: any): any {
    Logger.error(e);
    yield put(
        ShowToast({
            severity: 'error',
            message: `Error: ${e?.response?.data?.Error ?? e.message}`,
            duration: 6000,
            onClose: HideToast,
        })
    );
}

function* fetchTemplatesList(action: LoadListStartAction): any {
    const { type, page, limit, search, templateOrder, tags, templateTypes, isInfinityScroll } = action.payload;
    const offset = page === 1 ? 0 : (page - 1) * limit;
    const params: any = {
        type,
        offset,
        limit,
        templateOrder,
        tags,
        templateTypes,
    };

    if (!isEmpty(search)) {
        params.search = search;
    }

    try {
        const { templatesList } = yield select(getTemplateList);
        const previousTemplatesList = offset === 0 ? [] : templatesList;
        const list = yield call(loadTemplateList, params);
        const templates = [
            ...list.Templates,
            ...list.DraftTemplate,
            ...list.GlobalTemplate,
            ...list.ParentTemplates,
            ...list.PreviouslySentTemplates,
        ];
        const count =
            list.TemplatesCount +
            list.DraftTemplatesCount +
            list.GlobalTemplatesCount +
            list.ParentTemplatesCount +
            list.PreviouslySentTemplatesCount;

        const hasMore = templates.length === limit;

        yield put(
            LoadingTemplatesSuccess(
                isInfinityScroll ? [...previousTemplatesList, ...templates] : templates,
                count,
                hasMore
            )
        );
    } catch (e) {
        yield ShowErrorToast(e);

        yield put(LoadingTemplatesError());
    }
}
function* deleteSingleTemplate(action: templateDeleteStartAction): any {
    const { payload } = action;

    const offset = payload.page === 1 ? 0 : (payload.page - 1) * payload.limit;
    const params = {
        type: payload.type,
        offset,
        limit: payload.limit,
        search: payload.search,
        templateOrder: payload.templateOrder,
        tags: payload.tags,
        templateTypes: payload.templateTypes,
        page: payload.page,
    };
    try {
        yield call(deleteTemplate, payload.name);
        const templateList = yield select(getTemplateList);
        const updatedTemplateList = templateList.templatesList.filter((template) => template.Name !== payload.name);
        const count = templateList.count - 1;

        yield put(templateDeleteSuccess(payload.name, updatedTemplateList, count, params));
        yield put(
            ShowToast({
                message: i18n.t('templatedeleted'),
                severity: 'info',
                duration: 3000,
                position: 'bottom-left',
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(templateDeleteError());
        yield ShowErrorToast(e);
    }
}
function* deleteArrayOfTemplates(action: templateArrayDeleteStartAction): any {
    const { payload } = action;

    const offset = payload.page === 1 ? 0 : (payload.page - 1) * payload.limit;
    const params = {
        type: payload.type,
        offset,
        limit: payload.limit,
        search: payload.search,
        templateOrder: payload.templateOrder,
        tags: payload.tags,
        templateTypes: payload.templateTypes,
        page: payload.page,
    };
    try {
        yield call(deleteTemplateArray, payload.templatesToDelete);
        const templateList = yield select(getTemplateList);
        const count = templateList.count - payload.templatesToDelete.length;

        const updatedTemplateList = templateList.templatesList.filter(
            (template) =>
                payload.templatesToDelete.findIndex((templateToDelete) => template.Name === templateToDelete) === -1
        );

        yield put(templateArrayDeleteSuccess(updatedTemplateList, count, params));
        yield put(
            ShowToast({
                message: i18n.t('templatesdeleted'),
                severity: 'info',
                duration: 3000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(templateArrayDeleteError());
        yield ShowErrorToast(e);
    }
}
function* updateTemplateFunc(action): any {
    const { payload } = action;

    try {
        yield call(updateTemplate, payload.name, payload.template, payload.removeScreenshot);
        const { templatesList } = yield select(getTemplateList);

        const updatedTemplateList = templatesList.map((template: Template) => {
            if (template.Name === payload.template.Name) {
                //replace with new value
                return payload.template;
            }
            if (payload.template.IsDefault && payload.template.TemplateType === template.TemplateType) {
                //if selecting new default, mark previous not default
                return {
                    ...template,
                    IsDefault: false,
                };
            }

            return template;
        });
        yield put(templateUpdateSuccess(payload.name, updatedTemplateList));
    } catch (e) {
        yield put(templateUpdateError());
        yield ShowErrorToast(e);
    }
}
function* previewTemplateFunc(action: templatePreviewStartAction): any {
    const { payload } = action;
    const templateList: TemplateListState = yield select(getTemplateList);
    try {
        const name = safelyDecodeBase64Url(payload.name);
        const currentTemplate = templateList?.templatesList?.find((tmpl) => tmpl.Name === name);

        const template = yield call(previewTemplate, name, payload.ispublic, currentTemplate?.TemplateType);
        yield put(templatePreviewSuccess(name, template, payload.ispublic));
    } catch (e) {
        yield put(templatePreviewError());

        yield ShowErrorToast(e);
    }
}
function* addTemplate(action): any {
    const { payload } = action;
    const { isStandalone }: ConfigState = yield select(getConfigStore);

    try {
        const templateList = yield select(getTemplateList);
        const templates = templateList.templatesList;
        const template = yield call(copyTemplate, payload.prevTemplateName, payload.changes);

        const count = templateList.count + 1;

        yield put(AddTemplateSuccess(payload.template, templates, count));
        if (template.TemplateType === TemplateType.DragDropEditor) {
            const editorPath = isStandalone ? TemplatesPaths.LegacyDragDropEditor : TemplatesPaths.DragDropEditor;
            yield put(push(`${editorPath}/${safelyEncodeBase64Url(template.Name)}`));
        } else if (template.TemplateType === TemplateType.TemplateEditor) {
            yield put(
                push(
                    `${TemplatesPaths.TemplateEditorLinkName.replace(
                        pathsParams.template,
                        safelyEncodeBase64Url(template.Name)
                    )}`
                )
            );
        } else {
            yield put(push(`${TemplatesPaths.EditorLink}/${safelyEncodeBase64Url(template.Name)}`));
        }
        yield put(
            ShowToast({
                message: 'Template copied',
                severity: 'info',
                duration: 3000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(AddTemplateError());
        yield ShowErrorToast(e);
    }
}

function* shareTemplateFunc(action: ShareTemplateStartAction): any {
    const { payload } = action;

    try {
        const shareIdentifier = yield call(shareTemplate, payload.name);
        yield put(ShareTemplateSuccess(shareIdentifier.data));
    } catch (e) {
        yield put(ShareTemplateError());
        yield ShowErrorToast(e);
    }
}

function* getSharedTemplateFunc(action: CreateSharedTemplateStartAction): any {
    const { payload } = action;

    try {
        const templatesState = yield select(getTemplateList);
        const offset = templatesState.page === 1 ? 0 : (templatesState.page - 1) * templatesState.limit;
        const params: any = {
            type: templatesState.type,
            offset,
            limit: templatesState.limit,
            templateOrder: templatesState.templateOrder,
            tags: templatesState.tags,
            templateTypes: templatesState.templateTypes,
            page: templatesState.page,
        };

        if (!isEmpty(templatesState.search)) {
            params.search = templatesState.search;
        }

        const sharedTemplate = yield call(createSharedTemplate, payload.shareIdentifier);

        yield put(createSharedTemplateSuccess(sharedTemplate.data, params));

        yield put(
            ShowToast({
                message: i18n.t('sharetemplateadded', { sharedTemplateName: sharedTemplate.data.Name }),
                severity: 'info',
                duration: 3000,
                onClose: HideToast,
            })
        );
        yield put(replace({ search: null }));
    } catch (e) {
        yield ShowErrorToast(e);
    }
}

function* watchAddTemplate(): any {
    yield takeLatest(TemplatesActionTypes.SAVE_TEMPLATE_START, addTemplate);
}
function* watchFetchTemplateList(): any {
    yield takeLatest(TemplatesActionTypes.LOADING_START, fetchTemplatesList);
}
function* watchdeleteSingleTemplate(): any {
    yield takeLatest(TemplatesActionTypes.DELETE_START, deleteSingleTemplate);
}
function* watchdeleteSingleTemplateSuccess(): any {
    yield takeLatest(TemplatesActionTypes.DELETE_SUCCESS, fetchTemplatesList);
}
function* watchdeleteArrayOfTemplates(): any {
    yield takeLatest(TemplatesActionTypes.DELETE_ARRAY_START, deleteArrayOfTemplates);
}
function* watchdeleteArrayOfTemplatesSuccess(): any {
    yield takeLatest(TemplatesActionTypes.DELETE_ARRAY_SUCCESS, fetchTemplatesList);
}
function* watchPreviewTemplate(): any {
    yield takeLatest(TemplatesActionTypes.PREVIEW_START, previewTemplateFunc);
}
function* watchUpdateTemplate(): any {
    yield takeLatest(TemplatesActionTypes.UPDATE_START, updateTemplateFunc);
}
function* watchShareTemplate(): any {
    yield takeLatest(TemplatesActionTypes.SHARE_TEMPLATE_START, shareTemplateFunc);
}
function* watchCreateSharedTemplate(): any {
    yield takeLatest(TemplatesActionTypes.CREATE_SHARED_TEMPLATE_START, getSharedTemplateFunc);
}
function* watchCreateSharedTemplateSuccess(): any {
    yield takeLatest(TemplatesActionTypes.CREATE_SHARED_TEMPLATE_SUCCESS, fetchTemplatesList);
}
const templateListSagas = [
    fork(watchFetchTemplateList),
    fork(watchdeleteSingleTemplate),
    fork(watchdeleteArrayOfTemplates),
    fork(watchPreviewTemplate),
    fork(watchUpdateTemplate),
    fork(watchAddTemplate),
    fork(watchdeleteSingleTemplateSuccess),
    fork(watchdeleteArrayOfTemplatesSuccess),
    fork(watchShareTemplate),
    fork(watchCreateSharedTemplate),
    fork(watchCreateSharedTemplateSuccess),
];

export default templateListSagas;
